import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Select,
  Tag,
  Space,
  message,
  Modal,
  Spin,
  Tooltip,
  Card,
  Typography,
  Row,
  Col,
  Empty,
  Alert,
  Input,
  Popconfirm,
  Switch,
} from "antd";
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  PlusOutlined,
  DeleteOutlined,
  StarOutlined,
  StarFilled,
  CheckCircleOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  UndoOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  getAllUsersInfo,
  updateUserInfo,
} from "../api/internal_tool_endpoints";
import {
  hardDeleteUser,
  deactivateUser,
  reactivateUser,
  setUserDashmoteAdmin,
  setUserAccountType,
} from "../api/userManagementApi";
import AccountTypeSelect from "../components/AccountTypeSelect";
import GeneralLayout from "../components/GeneralLayout";
import NavBreadcrumb from "../components/NavBreadcrumb";
import ScopePicker from "@web/components/InternalConfigurationTable/inviteUsers/ScopePicker";
import { emptyProductScopes } from "../components/productScopes";
import { getRoleStyle, getRoleDisplayName } from "../utils/roleUtils";
import { isDashmoteStaffEmail } from "../utils/isDashmoteStaffEmail";
import { formatClientName } from "../features/dashai/lib/utils";
import { isSyntheticAccountType } from "../utils/accountType";
import { foldSearch } from "../utils/searchFold";

const { Option } = Select;
const { Text } = Typography;

// Consistent pill-shaped role tag matching UserManagement style
const RoleTag = ({ role }) => {
  const { borderColor, textColor, backgroundColor } = getRoleStyle(role);
  const displayName = getRoleDisplayName(role);
  return (
    <Tag
      style={{
        border: `1px solid ${borderColor}`,
        borderRadius: "60rem",
        background: backgroundColor,
        color: textColor,
        fontFamily: "Inter",
        fontSize: "0.75rem",
        fontWeight: 400,
        padding: "0 0.5rem",
        margin: "2px",
      }}
    >
      {displayName}
    </Tag>
  );
};

// Consistent workspace pill tag. `name` is the raw workspace id; it renders a
// portal-consistent display name — the id stays the key everywhere the
// tag is used for identity.
const WorkspaceTag = ({ name, isDefault, maxWidth = 180 }) => {
  const label = formatClientName(name);
  return (
    <Tooltip title={label}>
      <Tag
        style={{
          border: `1px solid ${isDefault ? "#0E49B5" : "rgba(0,0,0,0.15)"}`,
          borderRadius: "60rem",
          background: isDefault ? "#F5F8FC" : "#ffffff",
          color: isDefault ? "#0E49B5" : "rgba(0,0,0,0.65)",
          fontFamily: "Inter",
          fontSize: "0.75rem",
          fontWeight: 400,
          padding: "0 0.5rem",
          margin: "2px",
          // Cap width so a long workspace name (e.g. "Carlsberg Britvic Soft
          // Drinks") truncates with an ellipsis instead of overflowing and
          // breaking the row layout; the full name is on the tooltip. The cap
          // is caller-tunable so a narrow fixed-width column (the Default
          // Workspace column) can request a tighter bound than the flexible
          // Workspace Access column.
          maxWidth: `${maxWidth}px`,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "inline-block",
          verticalAlign: "middle",
        }}
      >
        {label}
      </Tag>
    </Tooltip>
  );
};

// Account-type pill for the user table's first column. `null`/absent = a normal
// account ("Regular"); "demo"/"test" are the synthetic classifications, coloured
// to match the account-type editor card (demo = purple, test = amber).
const ACCOUNT_TYPE_STYLES = {
  demo: { label: "Demo", color: "#722ed1", bg: "#f9f0ff", border: "#722ed1" },
  test: { label: "Test", color: "#d46b08", bg: "#fff7e6", border: "#ffa940" },
};
const REGULAR_ACCOUNT_STYLE = {
  label: "Regular",
  color: "rgba(0,0,0,0.65)",
  bg: "#ffffff",
  border: "rgba(0,0,0,0.15)",
};
const AccountTypeTag = ({ accountType }) => {
  const s = ACCOUNT_TYPE_STYLES[accountType] || REGULAR_ACCOUNT_STYLE;
  return (
    <Tag
      style={{
        border: `1px solid ${s.border}`,
        borderRadius: "60rem",
        background: s.bg,
        color: s.color,
        fontFamily: "Inter",
        fontSize: "0.75rem",
        fontWeight: 400,
        padding: "0 0.6rem",
        margin: 0,
      }}
    >
      {s.label}
    </Tag>
  );
};

// Matches commonButtonStyles from useUserUtils
const ACTION_BTN = {
  fontFamily: "Inter",
  fontSize: "14px",
  fontWeight: 500,
  color: "#0e49b5",
  padding: 0,
  height: "auto",
};

const DELETE_BTN = {
  ...ACTION_BTN,
  color: "#DC2626",
};

// Deactivate is reversible — amber "caution", visually distinct from the red
// permanent-delete action so the two reachable-from-the-same-row actions never
// read as the same button.
const DEACTIVATE_BTN = {
  ...ACTION_BTN,
  color: "#D97706",
};

export default function UserScopeManagementPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingData, setEditingData] = useState(null);
  // Workspace grants the user held when editing began. `handleSave` diffs the
  // in-memory grants against this to detect (and confirm) a Save that would
  // drop workspace access — the guard against an id_client clobber,
  // where the whole map is POSTed and any shrink is persisted wholesale.
  const originalWorkspacesRef = useRef([]);
  // Non-null while the drop-guard confirmation is open: the workspace keys a
  // Save would remove. A controlled <Modal> (not the imperative Modal.confirm)
  // so it renders in the React tree — testable, and it consumes theme context.
  const [pendingDropWorkspaces, setPendingDropWorkspaces] = useState(null);
  const [addingWorkspace, setAddingWorkspace] = useState(false);
  const [selectedWorkspaceToAdd, setSelectedWorkspaceToAdd] = useState(null);
  const [savingDashmoteAdmin, setSavingDashmoteAdmin] = useState(false);
  const [savingAccountType, setSavingAccountType] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  // Recycle-bin view: "active" (default) or "deactivated". /all-users
  // already returns delete_time for every user, so this filters client-side.
  const [statusFilter, setStatusFilter] = useState("active");

  const { hierarchicalWorkspaces } = useSelector((state) => state.workspace);

  const allWorkspaces = useMemo(
    () => Object.keys(hierarchicalWorkspaces || {}),
    [hierarchicalWorkspaces]
  );

  const getMarketsForWorkspace = useCallback(
    (workspaceId) => {
      if (!hierarchicalWorkspaces || !hierarchicalWorkspaces[workspaceId]) return [];
      const client = hierarchicalWorkspaces[workspaceId];
      const markets = [];
      Object.values(client.regions || {}).forEach((region) => {
        region.markets.forEach((market) => {
          if (!markets.includes(market)) markets.push(market);
        });
      });
      return markets;
    },
    [hierarchicalWorkspaces]
  );

  const editableRoles = ["super_admin", "admin", "normal_user"];

  const filteredUsers = useMemo(() => {
    // Recycle-bin filter first: a user is deactivated iff delete_time
    // is set. Then narrow by the email search.
    const byStatus = users.filter((u) =>
      statusFilter === "deactivated" ? !!u.delete_time : !u.delete_time
    );
    if (!searchText.trim()) return byStatus;
    const lower = foldSearch(searchText);
    return byStatus.filter((u) => foldSearch(u.email).includes(lower));
  }, [users, searchText, statusFilter]);

  // Header badge counts the CURRENT view, not all users — a deactivated user is
  // absent from the Active list, so a total-users count read as a mismatch
  // (56 in the header vs the deactivated user missing from the Active list).
  const statusCounts = useMemo(() => {
    let active = 0;
    let deactivated = 0;
    for (const u of users) u.delete_time ? deactivated++ : active++;
    return { active, deactivated };
  }, [users]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllUsersInfo();
      setUsers(response.users || []);
    } catch (error) {
      message.error("Failed to fetch users: " + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEdit = (user) => {
    setEditingUserId(user.id_user);
    originalWorkspacesRef.current = Object.keys(user.id_client || {});
    setEditingData({
      auth_method: "email_password",
      default_id_client: user.default_id_client,
      email: user.email,
      id_client: user.id_client || {},
      dashmote_admin: Boolean(user.dashmote_admin),
      account_type: user.account_type ?? null,
    });
    setAddingWorkspace(false);
    setSelectedWorkspaceToAdd(null);
  };

  // Flip the user-level dashmote_admin flag via the dedicated audited endpoint.
  // Applies immediately rather than waiting for Save, since it's a user-level
  // capability independent of the per-workspace scope edits.
  const handleDashmoteAdminToggle = async (checked) => {
    if (!editingUserId) return;
    setSavingDashmoteAdmin(true);
    try {
      await setUserDashmoteAdmin(editingUserId, checked);
      setEditingData((prev) =>
        prev ? { ...prev, dashmote_admin: checked } : prev
      );
      setUsers((prev) =>
        prev.map((u) =>
          u.id_user === editingUserId ? { ...u, dashmote_admin: checked } : u
        )
      );
      message.success(
        checked
          ? "Granted Dashmote admin access"
          : "Revoked Dashmote admin access"
      );
    } catch (error) {
      message.error(error.message);
    } finally {
      setSavingDashmoteAdmin(false);
    }
  };

  // Set/clear the user's account_type via the dedicated audited endpoint.
  // Applies immediately (like the admin toggle) — "demo" scopes a @dashmote.com
  // account to its workspaces, "test" hides a fixture from client lists, null
  // restores a normal account. A user-level change independent of the
  // per-workspace scope edits. `value` is null | "demo" | "test".
  const handleAccountTypeChange = async (value) => {
    if (!editingUserId) return;
    const accountType = value || null;
    setSavingAccountType(true);
    try {
      const result = await setUserAccountType(editingUserId, accountType);
      const nextType = result?.account_type ?? null;
      // Marking an account synthetic (demo/test) clears its dashmote_admin flag
      // server-side (a synthetic account can't hold /admin) — mirror that in
      // local state so the toggle reflects reality without a refetch.
      const patch = isSyntheticAccountType(nextType)
        ? { account_type: nextType, dashmote_admin: false }
        : { account_type: nextType };
      setEditingData((prev) => (prev ? { ...prev, ...patch } : prev));
      setUsers((prev) =>
        prev.map((u) =>
          u.id_user === editingUserId ? { ...u, ...patch } : u
        )
      );
      message.success(
        nextType ? `Set account type to ${nextType}` : "Cleared account type"
      );
    } catch (error) {
      message.error(error.message);
    } finally {
      setSavingAccountType(false);
    }
  };

  const handleCancel = () => {
    setEditingUserId(null);
    setEditingData(null);
    setAddingWorkspace(false);
    setSelectedWorkspaceToAdd(null);
    setPendingDropWorkspaces(null);
  };

  const doSave = async () => {
    try {
      await updateUserInfo({ email: editingData.email, user_info: editingData });
      message.success("User scope updated successfully");
      setEditingUserId(null);
      setEditingData(null);
      setAddingWorkspace(false);
      setSelectedWorkspaceToAdd(null);
      setPendingDropWorkspaces(null);
      fetchUsers();
    } catch (error) {
      message.error("Failed to update user scope: " + error.message);
    }
  };

  const handleSave = async () => {
    // `PUT /users/email` replaces id_client wholesale, so a Save persists
    // whatever grants editingData holds. Guard against dropping workspace
    // access — whether from an explicit Remove or an unintended collapse
    // by confirming the exact workspaces being removed first.
    const dropped = originalWorkspacesRef.current.filter(
      (ws) => !editingData.id_client?.[ws]
    );
    if (dropped.length > 0) {
      setPendingDropWorkspaces(dropped);
      return;
    }
    await doSave();
  };

  const handleDefaultWorkspaceChange = (workspace) =>
    setEditingData({ ...editingData, default_id_client: workspace });

  const handleConfirmAddWorkspace = () => {
    if (!selectedWorkspaceToAdd) { message.warning("Please select a workspace"); return; }
    if (editingData.id_client[selectedWorkspaceToAdd]) { message.warning("Workspace already exists"); return; }
    setEditingData({
      ...editingData,
      id_client: {
        ...editingData.id_client,
        [selectedWorkspaceToAdd]: {
          role: "normal_user",
          product_scopes: emptyProductScopes(),
        },
      },
    });
    setAddingWorkspace(false);
    setSelectedWorkspaceToAdd(null);
    message.success(`Workspace ${selectedWorkspaceToAdd} added`);
  };

  const handleRemoveWorkspace = (workspace) => {
    const newIdClient = { ...editingData.id_client };
    delete newIdClient[workspace];
    let newDefault = editingData.default_id_client;
    if (workspace === editingData.default_id_client) {
      const remaining = Object.keys(newIdClient);
      newDefault = remaining.length > 0 ? remaining[0] : "";
    }
    setEditingData({ ...editingData, default_id_client: newDefault, id_client: newIdClient });
    message.success(`Workspace ${workspace} removed`);
  };

  const handleWorkspaceRoleChange = (workspace, role) => {
    const isSuperAdmin = role === "super_admin";
    setEditingData({
      ...editingData,
      id_client: {
        ...editingData.id_client,
        [workspace]: {
          ...editingData.id_client[workspace],
          role,
          is_super_admin: isSuperAdmin,
          product_scopes: isSuperAdmin
            ? null
            : editingData.id_client[workspace]?.product_scopes || emptyProductScopes(),
        },
      },
    });
  };

  const handleProductScopesChange = (workspace, nextScopes) => {
    setEditingData({
      ...editingData,
      id_client: {
        ...editingData.id_client,
        [workspace]: {
          ...editingData.id_client[workspace],
          product_scopes: nextScopes,
        },
      },
    });
  };

  const renderWorkspaceCard = (workspace, workspaceData) => {
    const isDefault = editingData.default_id_client === workspace;
    const isSuperAdmin = workspaceData.role === "super_admin";
    const markets = getMarketsForWorkspace(workspace);

    return (
      <Card
        key={workspace}
        size="small"
        style={{
          marginBottom: "0.75rem",
          borderRadius: "12px",
          border: isDefault ? "1.5px solid #0E49B5" : "1px solid #e8e8e8",
          background: isDefault ? "#F5F8FC" : "#ffffff",
        }}
      >
        <div style={{ marginBottom: "0.75rem" }}>
          <Row gutter={16} align="middle" wrap={false}>
            <Col flex="auto" style={{ minWidth: 0 }}>
              <Space size="small">
                {isDefault ? (
                  <StarFilled style={{ color: "#0E49B5", fontSize: "14px" }} />
                ) : (
                  <StarOutlined
                    style={{ color: "#bfbfbf", fontSize: "14px", cursor: "pointer" }}
                    onClick={() => handleDefaultWorkspaceChange(workspace)}
                  />
                )}
                {/* Format the raw id (e.g. `carlsberg_britvic_beer`) to a display
                    name, consistent with the rest of the portal. The raw id is
                    still the key used for role/remove/default actions. */}
                <Text style={{ fontFamily: "Inter", fontSize: "14px", fontWeight: 500, color: "#262626" }}>
                  {formatClientName(workspace)}
                </Text>
                {isDefault && <Tag style={{ borderRadius: "60rem", background: "#F5F8FC", border: "1px solid #0E49B5", color: "#0E49B5", fontFamily: "Inter", fontSize: "0.7rem", padding: "0 0.5rem" }}>Default</Tag>}
              </Space>
            </Col>
            <Col flex="none">
              <Space size="small">
                <Select
                  aria-label="Workspace role"
                  value={workspaceData.role}
                  onChange={(value) => handleWorkspaceRoleChange(workspace, value)}
                  style={{ width: "160px", fontFamily: "Inter" }}
                  size="small"
                  labelRender={({ value }) => <RoleTag role={value} />}
                >
                  {editableRoles.map((r) => (
                    <Option key={r} value={r}>
                      <RoleTag role={r} />
                    </Option>
                  ))}
                </Select>
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveWorkspace(workspace)}
                  disabled={Object.keys(editingData.id_client).length === 1}
                  style={DELETE_BTN}
                >
                  Remove
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {!isSuperAdmin && workspaceData.product_scopes && (
          <ScopePicker
            role={workspaceData.role}
            markets={markets}
            productScopes={workspaceData.product_scopes}
            onChange={(next) => handleProductScopesChange(workspace, next)}
            showLabel={false}
          />
        )}
        {isSuperAdmin && (
          <Alert
            message="Super Admin has full access to all products and markets"
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginTop: "0.75rem", borderRadius: "8px" }}
          />
        )}
      </Card>
    );
  };

  const toggleExpanded = (userId) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const renderScopeDisplay = (user) => {
    const workspaces = Object.keys(user.id_client || {});
    if (workspaces.length === 0) return <Text style={{ fontFamily: "Inter", fontSize: "12px", color: "#8c8c8c" }}>No workspaces</Text>;
    const isExpanded = expandedUsers.has(user.id_user);
    const visible = isExpanded ? workspaces : workspaces.slice(0, 2);
    const hiddenCount = workspaces.length - 2;

    return (
      <Space size={[10, 8]} wrap style={{ width: "100%" }}>
        {visible.map((workspace) => {
          const workspaceData = user.id_client[workspace];
          const isDefault = user.default_id_client === workspace;
          return (
            <Space key={workspace} size={4}>
              {isDefault && <StarFilled style={{ color: "#0E49B5", fontSize: "11px" }} />}
              <WorkspaceTag name={workspace} isDefault={isDefault} />
              <RoleTag role={workspaceData.role} />
            </Space>
          );
        })}
        {hiddenCount > 0 && !isExpanded && (
          <Tag
            onClick={() => toggleExpanded(user.id_user)}
            style={{
              borderRadius: "60rem",
              fontFamily: "Inter",
              fontSize: "11px",
              border: "1px solid rgba(0,0,0,0.15)",
              background: "#fff",
              color: "rgba(0,0,0,0.45)",
              cursor: "pointer",
            }}
          >
            +{hiddenCount} more
          </Tag>
        )}
        {isExpanded && workspaces.length > 2 && (
          <Tag
            onClick={() => toggleExpanded(user.id_user)}
            style={{
              borderRadius: "60rem",
              fontFamily: "Inter",
              fontSize: "11px",
              border: "1px solid rgba(0,0,0,0.15)",
              background: "#fff",
              color: "rgba(0,0,0,0.45)",
              cursor: "pointer",
            }}
          >
            Show less
          </Tag>
        )}
      </Space>
    );
  };

  const renderScopeEditor = () => {
    if (!editingData) return null;
    const workspaces = Object.keys(editingData.id_client).sort((a, b) => {
      if (a === editingData.default_id_client) return -1;
      if (b === editingData.default_id_client) return 1;
      return a.localeCompare(b);
    });
    const availableWorkspaces = allWorkspaces.filter((w) => !workspaces.includes(w));
    // The default-workspace picker mirrors the per-card stars for an explicit-grant
    // user (same options, shared state), and widens to every workspace for an
    // implicit-access user (empty id_client), who has no cards and so no star.
    const defaultWorkspaceOptions = workspaces.length > 0 ? workspaces : allWorkspaces;

    return (
      <div style={{ padding: "0.75rem", background: "#fafafa", borderRadius: "8px" }}>
        {isDashmoteStaffEmail(editingData.email) && (
          <Card
            size="small"
            style={{
              marginBottom: "1rem",
              borderRadius: "12px",
              border: "2px solid #f5222d",
              background: "linear-gradient(to bottom, #fff1f0, #ffffff)",
            }}
          >
            <Row gutter={16} align="middle" wrap={false}>
              <Col flex="auto">
                <Space size="small">
                  <SafetyCertificateOutlined style={{ color: "#f5222d", fontSize: "18px" }} />
                  <Text strong style={{ fontSize: "15px", color: "#262626" }}>
                    Dashmote admin (/admin access)
                  </Text>
                </Space>
                <div style={{ marginTop: "4px" }}>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    User-level capability — gates the staff admin portal. Not a per-workspace role.
                  </Text>
                </div>
              </Col>
              <Col>
                {/* A synthetic (demo/test) account can't hold /admin — the flag
                    is cleared server-side when the account is marked synthetic,
                    and a grant would be rejected. Disable the toggle so it isn't
                    offered; the tooltip explains why. */}
                <Tooltip
                  title={
                    isSyntheticAccountType(editingData.account_type)
                      ? "A test/demo account can't hold /admin access. Set the account type to Regular first to change this."
                      : ""
                  }
                >
                  <span
                    style={{
                      cursor: isSyntheticAccountType(editingData.account_type)
                        ? "not-allowed"
                        : "default",
                    }}
                  >
                    <Switch
                      aria-label="Dashmote admin"
                      checked={Boolean(editingData.dashmote_admin)}
                      disabled={isSyntheticAccountType(editingData.account_type)}
                      loading={savingDashmoteAdmin}
                      onChange={handleDashmoteAdminToggle}
                      checkedChildren="Admin"
                      unCheckedChildren="Off"
                    />
                  </span>
                </Tooltip>
              </Col>
            </Row>
          </Card>
        )}
        {/* Account type — single field (demo XOR test XOR none). "demo" only
            applies to @dashmote.com staff (scopes them out of all-workspace
            access), so it's disabled off-domain; "test" hides a fixture from
            client lists and applies to anyone. */}
        <Card
          size="small"
          style={{
            marginBottom: "1rem",
            borderRadius: "12px",
            border: "2px solid #722ed1",
            background: "linear-gradient(to bottom, #f9f0ff, #ffffff)",
          }}
        >
          <Row gutter={16} align="middle" wrap={false}>
            <Col flex="auto">
              <Text strong style={{ fontSize: "15px", color: "#262626" }}>
                Account type
              </Text>
              <div style={{ marginTop: "4px" }}>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Regular — regular Dashmote accounts see every workspace, others
                  only their assigned ones. Demo — Dashmote email only, limited to
                  its assigned workspaces. Test — an internal account, hidden from
                  client-facing user lists.
                </Text>
              </div>
            </Col>
            <Col>
              <AccountTypeSelect
                value={editingData.account_type || null}
                onChange={handleAccountTypeChange}
                demoAllowed={isDashmoteStaffEmail(editingData.email)}
                loading={savingAccountType}
              />
            </Col>
          </Row>
        </Card>

        <Text style={{ fontFamily: "Inter", fontSize: "12px", fontWeight: 600, color: "#8c8c8c", display: "block", marginBottom: "0.5rem" }}>
          DEFAULT WORKSPACE
        </Text>
        <Select
          aria-label="Default workspace"
          style={{ width: "100%", fontFamily: "Inter", marginBottom: "1.25rem" }}
          size="middle"
          placeholder="Select a default workspace..."
          value={editingData.default_id_client || undefined}
          onChange={handleDefaultWorkspaceChange}
          showSearch
          optionFilterProp="label"
          options={defaultWorkspaceOptions.map((w) => ({ value: w, label: formatClientName(w) }))}
        />

        <Text style={{ fontFamily: "Inter", fontSize: "12px", fontWeight: 600, color: "#8c8c8c", display: "block", marginBottom: "0.75rem" }}>
          WORKSPACE ACCESS — {workspaces.length} {workspaces.length === 1 ? "workspace" : "workspaces"}
        </Text>

        {workspaces.length === 0 ? (
          <Empty description="No workspaces assigned" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          workspaces.map((workspace) => renderWorkspaceCard(workspace, editingData.id_client[workspace]))
        )}

        {!addingWorkspace && availableWorkspaces.length > 0 && (
          <Button
            type="text"
            icon={<PlusOutlined />}
            onClick={() => setAddingWorkspace(true)}
            style={{ ...ACTION_BTN, marginTop: "0.5rem" }}
          >
            Add Workspace
          </Button>
        )}

        {addingWorkspace && (
          <Card size="small" style={{ marginTop: "0.75rem", borderRadius: "8px", border: "1px solid #e8e8e8" }}>
            <Space direction="vertical" style={{ width: "100%" }} size="small">
              <Text style={{ fontFamily: "Inter", fontSize: "13px", fontWeight: 500 }}>Add workspace</Text>
              <Select
                style={{ width: "100%", fontFamily: "Inter" }}
                size="middle"
                placeholder="Search and select a workspace..."
                value={selectedWorkspaceToAdd}
                onChange={setSelectedWorkspaceToAdd}
                showSearch
                optionFilterProp="children"
              >
                {availableWorkspaces.map((workspace) => (
                  <Option key={workspace} value={workspace}>{workspace}</Option>
                ))}
              </Select>
              <Space style={{ justifyContent: "flex-end", width: "100%" }}>
                <Button size="small" onClick={() => { setAddingWorkspace(false); setSelectedWorkspaceToAdd(null); }} style={{ fontFamily: "Inter" }}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={handleConfirmAddWorkspace}
                  disabled={!selectedWorkspaceToAdd}
                  style={{ fontFamily: "Inter", background: "#0E49B5" }}
                >
                  Add
                </Button>
              </Space>
            </Space>
          </Card>
        )}
      </div>
    );
  };

  // Soft delete: reversible deactivation. Reload so the user moves to
  // the Deactivated view with a fresh delete_time.
  const handleDeactivate = async (record) => {
    try {
      await deactivateUser(record.email);
      message.success(
        `${record.email} has been deactivated. Restore them from the Deactivated view.`
      );
      fetchUsers();
    } catch (error) {
      message.error(error.message || "Failed to deactivate user.");
    }
  };

  // Reactivate a deactivated user — clears delete_time.
  const handleReactivate = async (record) => {
    try {
      await reactivateUser(record.email);
      message.success(`${record.email} has been reactivated.`);
      fetchUsers();
    } catch (error) {
      message.error(error.message || "Failed to reactivate user.");
    }
  };

  // Permanent purge from the Deactivated view (archived + team-alerted
  // server-side). Cannot be undone.
  const handlePermanentDelete = async (record) => {
    try {
      await hardDeleteUser(record.email);
      message.success(`User ${record.email} has been permanently deleted.`);
      setUsers((prev) => prev.filter((u) => u.id_user !== record.id_user));
    } catch (error) {
      message.error(error.message || "Failed to delete user.");
    }
  };

  const columns = [
    {
      title: "Account Type",
      dataIndex: "account_type",
      key: "account_type",
      width: 130,
      render: (value) => <AccountTypeTag accountType={value} />,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 230,
      render: (text) => <Text style={{ fontFamily: "Inter", fontSize: "14px" }}>{text}</Text>,
    },
    {
      title: "Default Workspace",
      dataIndex: "default_id_client",
      key: "default_id_client",
      width: 170,
      // Guard the empty case: without a default workspace `text` is blank, which
      // otherwise renders a lone star + an empty blue tag (reads as broken).
      render: (text) =>
        text ? (
          <Space size={4}>
            <StarFilled style={{ color: "#0E49B5", fontSize: "11px" }} />
            {/* Tighter cap than the default 180: this column is a fixed 170px
                and also carries the star + gap, so a max-length name capped at
                180 spills into the Workspace Access column. 130 keeps the tag
                (plus star, gap, and cell padding) inside the column; the full
                name stays available on the tooltip. */}
            <WorkspaceTag name={text} isDefault maxWidth={130} />
          </Space>
        ) : (
          <Text type="secondary" style={{ fontFamily: "Inter", fontSize: "14px" }}>
            —
          </Text>
        ),
    },
    {
      title: "Workspace Access",
      key: "scope",
      render: (_, record) =>
        editingUserId === record.id_user ? renderScopeEditor() : renderScopeDisplay(record),
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      fixed: "right",
      render: (_, record) => {
        if (editingUserId === record.id_user) {
          return (
            <Space direction="vertical" size={4}>
              <Button type="text" size="small" icon={<SaveOutlined />} onClick={handleSave} style={ACTION_BTN}>
                Save
              </Button>
              <Button type="text" size="small" icon={<CloseOutlined />} onClick={handleCancel} style={{ ...ACTION_BTN, color: "rgba(0,0,0,0.45)" }}>
                Cancel
              </Button>
            </Space>
          );
        }
        // Deactivated-view rows: Reactivate (restore) + permanent
        // Delete instead of Edit / Add Workspace / soft Delete.
        if (record.delete_time) {
          return (
            <Space direction="vertical" size={4}>
              <Button type="text" size="small" icon={<UndoOutlined />} onClick={() => handleReactivate(record)} style={ACTION_BTN}>
                Reactivate
              </Button>
              <Popconfirm
                title="Permanently delete user"
                description={`Permanently delete ${record.email}? Their data is archived and the team is notified. This cannot be undone.`}
                onConfirm={() => handlePermanentDelete(record)}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button type="text" size="small" icon={<DeleteOutlined />} style={DELETE_BTN}>
                  Delete permanently
                </Button>
              </Popconfirm>
            </Space>
          );
        }
        return (
          <Space direction="vertical" size={4}>
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} style={ACTION_BTN}>
              Edit
            </Button>
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => { handleEdit(record); setAddingWorkspace(true); }}
              style={ACTION_BTN}
            >
              Add Workspace
            </Button>
            <Popconfirm
              title="Deactivate user"
              description={`Deactivate ${record.email}? They lose access immediately but can be restored anytime from the Deactivated view.`}
              onConfirm={() => handleDeactivate(record)}
              okText="Deactivate"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
            >
              <Button type="text" size="small" icon={<StopOutlined />} style={DEACTIVATE_BTN}>
                Deactivate
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <GeneralLayout>
      <div className="w-[calc(100%-240px)] ml-[240px] min-h-[100vh]">
        <div style={{ padding: "2rem" }}>
          <NavBreadcrumb />
          <div className="flex items-center gap-4 mb-6">
            <Button
              icon={<ArrowLeftOutlined />}
              // Home, matching the breadcrumb above. This page is reached
              // from the homepage and the nav manifest gives it no `subPageOf`,
              // so Workspace Configuration is not its parent.
              onClick={() => navigate("/")}
              style={{ fontFamily: "Inter", borderRadius: "9999px", height: "2.5rem", paddingLeft: "1rem", paddingRight: "1.25rem" }}
            >
              Back
            </Button>
            <div className="text-[2.5rem] font-bold font-[Satoshi] text-[#000000d9]">
              User Scope Management
            </div>
            <Tag
              style={{
                fontFamily: "Inter",
                fontSize: "13px",
                padding: "4px 12px",
                borderRadius: "12px",
                fontWeight: 600,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
                color: "#ffffff",
              }}
            >
              {statusCounts[statusFilter]}{" "}
              {statusFilter === "deactivated" ? "Deactivated" : "Active"}{" "}
              {statusCounts[statusFilter] === 1 ? "User" : "Users"}
            </Tag>
          </div>

          <div className="bg-white p-6 rounded-3xl border border-[rgba(0,0,0,0.05)] shadow-[2px_4px_12px_0px_rgba(0,0,0,0.02)]">
            <div className="flex items-center gap-4" style={{ marginBottom: "1.5rem" }}>
              <Input
                placeholder="Search by email..."
                prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                style={{ width: "320px", maxWidth: "320px", height: "3.25rem", borderRadius: "9999px", fontFamily: "Inter", fontSize: "14px" }}
                size="large"
              />
              {/* Recycle-bin filter, left beside the search. Custom
                  pill toggle matching the main app's (apps/web) User Management
                  view toggle — a plain button group rather than an AntD
                  Segmented, so the global dark-nav Segmented theme can't render
                  the labels white-on-white on this light page. */}
              <div className="h-[3.25rem] border border-[rgba(0,0,0,0.15)] p-[0.19rem] inline-flex gap-1 rounded-full min-w-fit">
                {[
                  { label: "Active", value: "active" },
                  { label: "Deactivated", value: "deactivated" },
                ].map(({ label, value }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setStatusFilter(value)}
                    style={{
                      color:
                        statusFilter === value ? "#0e49b5" : "rgba(0, 0, 0, 0.45)",
                    }}
                    className={`h-[2.75rem] px-6 flex items-center text-base rounded-full box-border font-medium transition-all duration-100 ease-in-out whitespace-nowrap ${
                      statusFilter === value
                        ? "bg-[#f5f8fc] border-[1.8px] border-[#0e49b5] text-[#0e49b5]"
                        : "border-[1.8px] border-[#fff]"
                    } hover:bg-gray-50 cursor-pointer`}
                  >
                    {label}
                  </button>

                ))}
              </div>
            </div>
            <Spin spinning={loading}>
              <Table
                columns={columns}
                dataSource={filteredUsers}
                rowKey="id_user"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} users`,
                }}
                scroll={{ x: 1200 }}
                size="middle"
                rowClassName={(record) => editingUserId === record.id_user ? "editing-row" : ""}
              />
            </Spin>
          </div>
        </div>
      </div>
      <Modal
        open={!!pendingDropWorkspaces}
        title="Remove workspace access?"
        okText="Remove & save"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
        onOk={() => doSave()}
        onCancel={() => setPendingDropWorkspaces(null)}
      >
        <Typography.Paragraph>
          Saving will remove this user&apos;s access to:{" "}
          <Text strong>{(pendingDropWorkspaces || []).join(", ")}</Text>. This
          replaces their workspace grants and cannot be undone from here.
        </Typography.Paragraph>
      </Modal>
      <style>{`
        .editing-row { background-color: #F5F8FC; }
        .editing-row td { background-color: #F5F8FC !important; }
      `}</style>
    </GeneralLayout>
  );
}
